import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from './button';

/**
 * A side sheet on the native modal <dialog>: focus moves in and is trapped, Escape closes, focus returns to the opener.
 * On narrow screens it rises from the bottom. `footer` holds the actions so they stay visible while the body scrolls.
 */
export function Sheet({ open, onClose, title, description, icon, footer, children }: {
  open: boolean; onClose: () => void; title: string; description?: string; icon?: ReactNode; footer?: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId(); const descriptionId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return <dialog ref={ref} className="sheet" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined}
    onClose={onClose} onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    {open && <div className="sheet-panel">
      <header className="sheet-header">
        {icon}
        <div><h2 id={titleId}>{title}</h2>{description && <p id={descriptionId}>{description}</p>}</div>
        <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose}><X aria-hidden /></Button>
      </header>
      <div className="sheet-body">{children}</div>
      {footer && <footer className="sheet-footer">{footer}</footer>}
    </div>}
  </dialog>;
}

/** A small confirm step for actions that delete something. The confirm button names the action. */
export function ConfirmDialog({ open, title, body, confirmLabel, busy, onConfirm, onClose }: {
  open: boolean; title: string; body: string; confirmLabel: string; busy?: boolean; onConfirm: () => void; onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId(); const bodyId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return <dialog ref={ref} className="confirm-dialog" role="alertdialog" aria-labelledby={titleId} aria-describedby={bodyId}
    onClose={onClose} onCancel={event => { event.preventDefault(); onClose(); }}>
    {open && <>
      <h2 id={titleId}>{title}</h2><p id={bodyId}>{body}</p>
      <div className="confirm-actions">
        <Button variant="outline" onClick={onClose} autoFocus>Cancel</Button>
        <Button variant="danger" disabled={busy} onClick={onConfirm}>{busy ? 'Working…' : confirmLabel}</Button>
      </div>
    </>}
  </dialog>;
}
